/**
 * Helpful resources:
 * https://github.com/randlabs/myalgo-connect
 */
import BaseWallet from './base'
import type MyAlgoConnect from '@randlabs/myalgo-connect'
import type { InitAlgodClient } from './base'
import { PROVIDER_ID, NODE_TOKEN, NODE_SERVER, NODE_PORT } from '../constants'
import walletProviders from '../providers'
import algosdk from 'algosdk'
import type { WalletProvider } from '../../types/wallet'
import { TransactionsArray } from 'types/api'

type InitWallet = {
  id: PROVIDER_ID
  client: MyAlgoConnect
  providers: typeof walletProviders
}

const initAlgodClient: InitAlgodClient = {
  algosdk,
  token: NODE_TOKEN,
  server: NODE_SERVER,
  port: NODE_PORT
}

class MyAlgoWalletClient extends BaseWallet {
  #client: MyAlgoConnect
  id: PROVIDER_ID
  provider: WalletProvider

  constructor(initAlgodClient: InitAlgodClient, initWallet: InitWallet) {
    super(initAlgodClient)

    this.#client = initWallet.client
    this.id = initWallet.id
    this.provider = initWallet.providers[this.id]
  }

  static async init() {
    const MyAlgo = (await import('@randlabs/myalgo-connect')).default
    const myAlgo = new MyAlgo({ disableLedgerNano: false })

    const initWallet: InitWallet = {
      id: PROVIDER_ID.MYALGO_WALLET,
      client: myAlgo,
      providers: walletProviders
    }

    return new MyAlgoWalletClient(initAlgodClient, initWallet)
  }

  async connect() {
    const accounts = await this.#client.connect()

    if (accounts.length === 0) {
      throw new Error(`No accounts found for ${this.provider}`)
    }

    const mappedAccounts = accounts.map((account) => ({
      ...account,
      providerId: this.provider.id
    }))

    return {
      ...this.provider,
      accounts: mappedAccounts
    }
  }

  async reconnect() {
    return null
  }

  async disconnect() {
    console.log('disconnected')
  }

  async signTransactions(transaction: string[]) {
    const result = await this.#client.signTransaction(transaction)

    if (!result) {
      throw new Error('Signing failed.')
    }

    return result.map(({ blob }) => blob)
  }

  async signEncodedTransactions(transactions: TransactionsArray) {
    const transactionsToSign: string[] = []
    const signedRawTransactions: Uint8Array[] = []

    for (const [type, txn] of transactions) {
      if (type === 'u') {
        transactionsToSign.push(txn)
      }
    }

    const result = await this.#client.signTransaction(transactionsToSign)

    if (!result) {
      throw new Error('Signing failed.')
    }

    let resultIndex = 0

    for (const [type, txn] of transactions) {
      if (type === 'u') {
        signedRawTransactions.push(result[resultIndex].blob)
        resultIndex++
      } else {
        signedRawTransactions.push(new Uint8Array(Buffer.from(txn, 'base64')))
      }
    }

    return signedRawTransactions
  }
}

export default MyAlgoWalletClient.init().catch((e) => {
  if (typeof window !== 'undefined') {
    throw e
  }
})
